import { PlainButton, Tooltip } from "@jugl-web/ui-kit";
import { ErrorBoundary } from "@jugl-web/utils/utils/ErrorBoundary";
import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import { useChatBot } from "@jugl-web/domain-resources/chat-bot";
import { AIAssistantButton } from "@web-src/modules/ai-assistant/AIAssistantButton";
import { useUserRole } from "@web-src/modules/common/hooks/useUserRole";
import { SingletonPopoverProvider } from "@web-src/modules/common/providers/SingletonPopoverProvider";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import { TaskTimeTrackerNavbarButton } from "@web-src/modules/tasks/components/TaskTimeTrackerNavbarButton";
import { useTheme } from "@web-src/themes/useTheme";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import { useCXSignupDialog } from "@web-src/modules/cx/components/CXSignupDialog/useCXSignupDialog";
import { OnboardingFlowTooltip } from "@web-src/modules/cx/components/OnboardingFlowTooltip";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { useSidebarNav } from "@web-src/modules/navigation/providers/SidebarNavProvider";
import { ReactComponent as LogoIcon } from "./assets/logo.svg";
import { ReactComponent as JuglCxLogoIcon } from "./assets/jugl-cx-logo.svg";
import { ReactComponent as UserIcon } from "./assets/user.svg";
import { ReactComponent as HamburgerIcon } from "./assets/hamburger.svg";
import { ReactComponent as PricingIcon } from "./components/CXProfilePopover/assets/pricing.svg";
import { ReactComponent as SupportChatIcon } from "./components/CXProfilePopover/assets/support-chat.svg";
import { EntitySubscriptionInfoItem } from "./components/EntitySubscriptionInfoItem";
import { ProfilePopover } from "./components/ProfilePopover";
import { SelectEntityPopover } from "./components/SelectEntityPopover/SelectEntityPopover";
import { AnonymousSelectEntityPlaceholder } from "./components/AnonymousSelectEntityPlaceholder";
import { CXProfilePopover } from "./components/CXProfilePopover";

export interface MainNavBarProps {
  /**
   * Renders the mobile hamburger button. Set it only when a Home/CX sidebar is
   * mounted next to the nav bar, otherwise the button has nothing to open.
   */
  hasSidebarNav?: boolean;
}

export const MainNavBar = ({ hasSidebarNav = false }: MainNavBarProps) => {
  const { tcx } = useTheme();
  const { isCx } = useAppVariant();
  const { isTravelLogEmployee } = useUserRole();
  const { entity, subscriptionInfo } = useEntityProvider();
  const { isAnonymous } = useAnonymousSession();
  const cxSignupDialog = useCXSignupDialog();
  const { isOverlayOpen } = useSidebarNav();

  return (
    <SingletonPopoverProvider>
      <div
        className={tcx("relative h-[48px]", isOverlayOpen && "z-50", {
          light: "bg-primary-600",
          dark: "bg-[#181818]",
          blue: "bg-primary-800",
        })}
      >
        <div className="flex h-[48px] w-full items-center justify-between pr-3 lg:pr-6">
          <div className="flex h-full min-w-0 items-center justify-start gap-1 lg:gap-3">
            {hasSidebarNav && <SidebarHamburgerButton />}
            {isAnonymous ? (
              <AnonymousSelectEntityPlaceholder />
            ) : (
              <SelectEntityPopover />
            )}
          </div>
          <span className="flex shrink-0 items-center gap-4">
            {!isAnonymous && (
              <div className="hidden lg:flex">
                <TaskTimeTrackerNavbarButton />
              </div>
            )}
            <div className="hidden lg:flex">
              <EntitySubscriptionInfoItem />
            </div>
            {!isCx &&
              !isTravelLogEmployee &&
              entity &&
              subscriptionInfo?.planIsActive && (
                <div className="hidden lg:flex">
                  <ErrorBoundary fallback={null}>
                    <AIAssistantButton />
                  </ErrorBoundary>
                </div>
              )}
            {isCx && !isAnonymous && (
              <div className="hidden lg:flex">
                <CXSeePlansButton />
              </div>
            )}
            {isCx && (
              <div className="hidden lg:flex">
                <CXSupportChatButton />
              </div>
            )}
            {isCx && (
              <OnboardingFlowTooltip
                flow="postPurchase"
                step={1}
                placement="bottom-end"
              >
                <CXProfilePopover />
              </OnboardingFlowTooltip>
            )}
            {isAnonymous ? (
              <PlainButton
                onClick={() => cxSignupDialog?.open?.()}
                className={cx(
                  "flex h-8 items-center justify-center rounded-lg px-2 transition-colors",
                  cxSignupDialog && "hover:bg-[#333333]"
                )}
              >
                <UserIcon />
              </PlainButton>
            ) : (
              <ProfilePopover />
            )}
            <div className="ml-4 hidden items-center gap-1 lg:flex">
              {isCx ? (
                <JuglCxLogoIcon className="h-[23px]" />
              ) : (
                <div className="pt-1.5">
                  <LogoIcon className="h-[18px]" />
                </div>
              )}
            </div>
          </span>
        </div>
      </div>
    </SingletonPopoverProvider>
  );
};

const topBarIconButtonClassName =
  "flex h-8 w-8 items-center justify-center rounded-lg outline-none transition-colors hover:bg-[#333333]";

const SidebarHamburgerButton = () => {
  const { tcx } = useTheme();
  const { isOverlayOpen, toggleOverlay } = useSidebarNav();

  return (
    <PlainButton
      className={tcx(
        "ml-3 flex h-8 w-8 items-center justify-center rounded-lg transition-colors outline-none lg:hidden",
        {
          light: ["hover:bg-primary-700", { "bg-primary-700": isOverlayOpen }],
          dark: ["hover:bg-[#333333]", { "bg-[#333333]": isOverlayOpen }],
          blue: ["hover:bg-primary-700", { "bg-primary-700": isOverlayOpen }],
        }
      )}
      onClick={toggleOverlay}
    >
      <HamburgerIcon />
    </PlainButton>
  );
};

const CXSeePlansButton = () => {
  const { t } = useTranslations();
  const { isAdmin } = useUserRole();
  const { plans, openSubscriptionDrawer } = useSubscriptionV2();

  if (!isAdmin || !plans?.length) return null;

  return (
    <Tooltip
      renderTrigger={({ props, ref }) => (
        <PlainButton
          ref={ref}
          {...props}
          className={topBarIconButtonClassName}
          onClick={() => openSubscriptionDrawer()}
        >
          <PricingIcon />
        </PlainButton>
      )}
    >
      {t({
        id: "main-nav-bar-component.see-plans",
        defaultMessage: "See Plans",
      })}
    </Tooltip>
  );
};

const CXSupportChatButton = () => {
  const { t } = useTranslations();
  const { isInitialized, show } = useChatBot();

  if (!isInitialized) return null;

  return (
    <Tooltip
      renderTrigger={({ props, ref }) => (
        <PlainButton
          ref={ref}
          {...props}
          className={topBarIconButtonClassName}
          onClick={() => show()}
        >
          <SupportChatIcon />
        </PlainButton>
      )}
    >
      {t({
        id: "main-nav-bar-component.support-chat",
        defaultMessage: "Support Chat",
      })}
    </Tooltip>
  );
};
